import React, { useEffect, useState } from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Box, Checkbox, Grid, Typography } from '@mui/material';
import { PropertyField as Field, PropertyField } from '../../system/field';
import { StatusDescriptions } from '../../constants/Status';
import { toFormattedPrice, toStandardDateFormat } from '../../utils';
import FullButton from '../../components/common/FullButton';
import { useService } from '../../contexts/ServiceContext';

interface ListingSummaryProps {
  onSubmit: (loading: (isLoading: boolean) => void) => void;
  getFieldValue: (key: PropertyField, fallback: string | undefined) => string;
}

const ListingSummary: React.FC<ListingSummaryProps> = ({
  onSubmit,
  getFieldValue,
}) => {
  const labelStyle = {
    fontWeight: 600,
    color: 'grey.500',
    mb: 0.5,
  };

  const { getListingRemarksHistory, isRemarkGenerated } = useService();
  const [aIRemarksAcknowledged, setAIRemarksAcknowledged] = useState(false);
  const [showAIListingRemarksAck, setShowAIListingRemarksAkt] =
    useState(isRemarkGenerated());

  const price = getFieldValue(Field.LIST_PRICE, 'Not Set');
  const listingKey = getFieldValue(Field.LISTING_KEY, '');
  const address = getFieldValue(PropertyField.FULL_STREET_ADDRESS, 'Not Set');

  // Get raw status and Office Exclusive setting
  const rawStatus = getFieldValue(PropertyField.MLS_STATUS, 'Not Set');
  const isOfficeExclusive = Boolean(
    getFieldValue(PropertyField.OFFICE_EXCLUSIVE_YN, ''),
  );

  // Apply masking rules based on Office Exclusive setting
  let displayStatus = rawStatus.replace('-BRIGHT', '');

  if (isOfficeExclusive) {
    if (displayStatus === 'ACTIVE') {
      displayStatus = 'Office Exclusive';
    } else if (displayStatus !== 'Not Set') {
      displayStatus = `${displayStatus} (Office Exclusive)`;
    }
  } else if (displayStatus === 'OFFICE EXCLUSIVE') {
    displayStatus = 'ACTIVE';
  }

  useEffect(() => {
    getListingRemarksHistory(listingKey).then((remarks) =>
      setShowAIListingRemarksAkt(
        (isEnabled) => isEnabled || remarks?.remarkshistory?.length > 0,
      ),
    );
  }, [getListingRemarksHistory, listingKey]);

  const expirationDate = getFieldValue(Field.EXPIRATION_DATE, 'Not Set');

  const formattedExpirationDate =
    expirationDate === 'Not Set'
      ? expirationDate
      : toStandardDateFormat(expirationDate, 'MMM DD, YYYY');

  const formattedPrice = price === 'Not Set' ? price : toFormattedPrice(price);

  const name = (firstNameKey: PropertyField, lastNameKey: PropertyField) => {
    const firstName = getFieldValue(firstNameKey, undefined);
    const lastName = getFieldValue(lastNameKey, undefined);
    if (!firstName && !lastName) {
      return undefined;
    }
    return `${firstName ? `${firstName} ` : ''}${lastName ? lastName : ''}`;
  };

  const participants = [
    name(
      PropertyField.LIST_AGENT_FIRST_NAME,
      PropertyField.LIST_AGENT_LAST_NAME,
    ),
    name(
      PropertyField.CO_LIST_AGENT_FIRST_NAME,
      PropertyField.CO_LIST_AGENT_LAST_NAME,
    ),
    name(
      PropertyField.BUYER_AGENT_FIRST_NAME,
      PropertyField.BUYER_AGENT_LAST_NAME,
    ),
    name(
      PropertyField.CO_BUYER_AGENT_FIRST_NAME,
      PropertyField.CO_BUYER_AGENT_LAST_NAME,
    ),
    getFieldValue(PropertyField.OWNER_NAME, undefined),
    getFieldValue(PropertyField.OWNER_1_NAME, undefined),
    getFieldValue(PropertyField.OWNER_2_NAME, undefined),
  ]
    .filter((name) => !!name)
    .join(', ');

  // Get description for the base status (without OE modifications)
  const baseStatus = rawStatus.replace('-BRIGHT', '');
  const formattedStatus =
    StatusDescriptions[baseStatus] ||
    'You can set the status in Listing Attributes';

  return (
    <>
      <Box
        sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3, mb: 8 }}
      >
        {/* Header */}
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Listing Summary
          </Typography>

          {/* Address Section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={labelStyle}>
              Address
            </Typography>
            <Typography variant="body2">{address}</Typography>
          </Box>

          {/* Status Section */}
          <Box>
            <Typography variant="body2" sx={labelStyle}>
              Status
            </Typography>
            <Typography variant="body2">
              {displayStatus} - {formattedStatus}
            </Typography>
          </Box>
        </Box>

        {/* Transaction Participants */}
        <Box>
          <Typography variant="body2" sx={labelStyle}>
            Transaction Participants
          </Typography>
          <Typography variant="body2">{participants}</Typography>
        </Box>

        {/* Price and Date */}
        <Grid container spacing={2}>
          <Grid size={6}>
            <Typography variant="body2" sx={labelStyle}>
              List Price
            </Typography>
            <Typography variant="body2">{formattedPrice}</Typography>
          </Grid>
          <Grid size={6}>
            <Typography variant="body2" sx={labelStyle}>
              Expiration Date
            </Typography>
            <Typography variant="body2">{formattedExpirationDate}</Typography>
          </Grid>
        </Grid>

        {/* Ai Generated Remarks acknowledgment */}
        {showAIListingRemarksAck && (
          <Box sx={{ display: 'flex' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-around',
              }}
            >
              <Checkbox
                onChange={(e) => setAIRemarksAcknowledged(e.target.checked)}
              />
            </Box>
            <Typography variant="body2">
              I acknowledge that I performed a personal review of the
              AI-generated public remarks, and to the best of my knowledge, they
              are accurate and compliant.
            </Typography>
          </Box>
        )}

        {/* Insights Section */}
        <Box
          sx={{
            bgcolor: 'rgba(25, 118, 210, 0.08)',
            borderRadius: 1,
            p: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AutoAwesomeIcon sx={{ color: 'primary.main' }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Insights
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Did you know listings with 30+ photos sell 15% faster on average?
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'primary.main', fontWeight: 500 }}
          >
            Boost interest in your listing by adding more photos!
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          width: 375,
          p: '12px 12px 16px',
          borderTop: '1px solid',
          borderColor: 'grey.300',
          backgroundColor: '#f0f0f0',
        }}
      >
        <FullButton
          disabled={showAIListingRemarksAck && !aIRemarksAcknowledged}
          text={'Publish Listing'}
          onClick={onSubmit}
        ></FullButton>
      </Box>
    </>
  );
};

export default ListingSummary;
